import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { AuthEntity } from '_entities';
import { IsAzAccount } from 'modules';
import { useIsReadyAndOffline } from 'hooks';

import { getMenuRoutes, filterRoutesByPermissions, useNavigationRail } from '../_utils';
import SubPagesPopup from '../SubPagesPopup';
import RailIcon from './icons';
import * as Styled from './styles';

const {
  getAuthenticatedUser,
  getUserEmail,
  getProfilePermissions,
  getModularityPermissions,
  getDashboardLinks,
  getCanAccessAppDashboard,
  getIsAppDashboardOnlyUser,
} = AuthEntity.selectors;

const TOP_LEVEL_MAX_ID = 6;

const matchesPath = (pathname, path) => {
  if (!path) return false;
  if (pathname === path) return true;
  return pathname.startsWith(path === '/' ? path : `${path}/`);
};

const isSectionActive = (section, pathname) => {
  if (section.path && matchesPath(pathname, section.path)) return true;
  if (section.subRoutes) {
    return section.subRoutes.some((sub) => matchesPath(pathname, sub.path));
  }
  return false;
};

const RailItemButton = memo(
  ({ section, active, hasSubpages, collapsed, popupOpen, currentPathname, onActivate, onClosePopup }) => (
    <Styled.RailItem>
      {active && <Styled.ActiveAccent aria-hidden />}
      <Styled.RailLink
        {...(section.path && !hasSubpages ? { as: Link, to: section.path } : { type: 'button' })}
        $active={active}
        $showTooltip={collapsed && !popupOpen}
        aria-current={active ? 'page' : undefined}
        aria-label={collapsed ? section.title : undefined}
        aria-haspopup={hasSubpages ? 'menu' : undefined}
        aria-expanded={hasSubpages ? popupOpen : undefined}
        onClick={(e) => onActivate(section, e)}
      >
        <Styled.IconWrap>
          <RailIcon name={section.title} />
        </Styled.IconWrap>
        {!collapsed && <Styled.RailLabel>{section.title}</Styled.RailLabel>}
        {collapsed && !popupOpen && <Styled.Tooltip role="tooltip">{section.title}</Styled.Tooltip>}
        {hasSubpages && (
          <Styled.SubpagesArrow aria-hidden>
            <svg width="8" height="10" viewBox="0 0 8 10" fill="none">
              <path
                d="M2 1.5 6 5 2 8.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Styled.SubpagesArrow>
        )}
      </Styled.RailLink>
      {popupOpen && <SubPagesPopup section={section} currentPathname={currentPathname} onClose={onClosePopup} />}
    </Styled.RailItem>
  ),
);

RailItemButton.displayName = 'RailItemButton';

RailItemButton.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    path: PropTypes.string,
    subRoutes: PropTypes.array,
  }).isRequired,
  active: PropTypes.bool.isRequired,
  hasSubpages: PropTypes.bool.isRequired,
  collapsed: PropTypes.bool.isRequired,
  popupOpen: PropTypes.bool.isRequired,
  currentPathname: PropTypes.string.isRequired,
  onActivate: PropTypes.func.isRequired,
  onClosePopup: PropTypes.func.isRequired,
};

const Rail = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const isOffline = useIsReadyAndOffline();
  const isAuthenticated = useSelector(getAuthenticatedUser);
  const userEmail = useSelector(getUserEmail);
  const userPermissions = useSelector(getProfilePermissions);
  const modularityPermissions = useSelector(getModularityPermissions);
  const dashboardInfoSections = useSelector(getDashboardLinks);
  const canAccessAppDashboard = useSelector(getCanAccessAppDashboard);
  const isAppDashboardOnlyUser = useSelector(getIsAppDashboardOnlyUser);
  const isAzAccount = IsAzAccount();
  const { collapsed, toggle } = useNavigationRail();

  const railRef = useRef(null);
  const [openPopupId, setOpenPopupId] = useState(null);

  const sections = useMemo(() => {
    const routes = getMenuRoutes(
      isAuthenticated,
      userEmail,
      isOffline,
      dashboardInfoSections,
      isAzAccount,
      canAccessAppDashboard,
      isAppDashboardOnlyUser,
    );
    return filterRoutesByPermissions(routes, userPermissions, modularityPermissions).filter(
      (item) => typeof item.id === 'number' && item.id <= TOP_LEVEL_MAX_ID,
    );
  }, [
    isAuthenticated,
    userEmail,
    isOffline,
    dashboardInfoSections,
    isAzAccount,
    canAccessAppDashboard,
    isAppDashboardOnlyUser,
    userPermissions,
    modularityPermissions,
  ]);

  // Auto-close the popup when navigation changes the route.
  useEffect(() => {
    setOpenPopupId(null);
  }, [pathname]);

  // Close on outside click.
  useEffect(() => {
    if (openPopupId === null) return undefined;
    const handleMouseDown = (event) => {
      if (railRef.current && !railRef.current.contains(event.target)) {
        setOpenPopupId(null);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [openPopupId]);

  const handleActivate = useCallback(
    (section, e) => {
      const hasSubpages = Array.isArray(section.subRoutes) && section.subRoutes.length > 0;
      if (hasSubpages) {
        setOpenPopupId((prev) => (prev === section.id ? null : section.id));
        return;
      }
      // Direct sections render as links, so let the browser own modified clicks (new tab/window) and
      // right-click; only a plain left-click navigates in place.
      if (e && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)) return;
      if (e) e.preventDefault();
      if (section.path) {
        history.push(section.path);
      }
      setOpenPopupId(null);
    },
    [history],
  );

  const handleClosePopup = useCallback(() => setOpenPopupId(null), []);

  if (!isAuthenticated || sections.length === 0) return null;

  return (
    <Styled.RailContainer ref={railRef} aria-label="Primary" $collapsed={collapsed}>
      <Styled.RailItems>
        {sections.map((section) => {
          const hasSubpages = Array.isArray(section.subRoutes) && section.subRoutes.length > 0;
          return (
            <RailItemButton
              key={section.id}
              section={section}
              active={isSectionActive(section, pathname)}
              hasSubpages={hasSubpages}
              collapsed={collapsed}
              popupOpen={openPopupId === section.id}
              currentPathname={pathname}
              onActivate={handleActivate}
              onClosePopup={handleClosePopup}
            />
          );
        })}
      </Styled.RailItems>
      <Styled.CollapseToggle
        type="button"
        aria-label={collapsed ? 'Expand navigation rail' : 'Collapse navigation rail'}
        aria-pressed={collapsed}
        onClick={toggle}
      >
        {collapsed ? '»' : '«'}
      </Styled.CollapseToggle>
    </Styled.RailContainer>
  );
};

export default Rail;
